import React, { useCallback, useEffect, useState } from 'react'
import { Alert, type AlertVariant } from '@components/ui'
import { NotificationPriority } from '@dataTypes/notification.types'
import './NotificationToast.css'

export interface NotificationToastData {
  id: string
  priority: NotificationPriority
  title: string
  date: string
  time: string
  message: string
  actionButtonText: string
  secondaryButtonText?: string
  icon?: string
  onAction?: () => void
  onSecondaryAction?: () => void
}

interface NotificationToastProps {
  notification: NotificationToastData
  onClose: (id: string) => void
  onAction?: (id: string) => void
}

const getAlertVariant = (priority: NotificationPriority): AlertVariant => {
  switch (priority) {
    case NotificationPriority.CRITICAL:
      return 'critical'
    case NotificationPriority.URGENT:
      return 'urgent'
    case NotificationPriority.SUCCESS:
      return 'success'
    default:
      return 'info'
  }
}

const NotificationToast: React.FC<NotificationToastProps> = ({
  notification,
  onClose,
  onAction,
}) => {
  const [isExiting, setIsExiting] = useState(false)

  const handleClose = useCallback(() => {
    setIsExiting(true)
    setTimeout(() => {
      onClose(notification.id)
    }, 300)
  }, [notification.id, onClose])

  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose()
    }, 5000)

    return () => clearTimeout(timer)
  }, [handleClose])

  const hasPrimaryAction = Boolean(notification.actionButtonText)
  const hasSecondaryAction = Boolean(notification.secondaryButtonText)

  return (
    <Alert
      className="notification-toast"
      layout="toast"
      variant={getAlertVariant(notification.priority)}
      title={notification.title}
      timestamp={`${notification.date} • ${notification.time}`}
      description={notification.message}
      animate={!isExiting}
      exiting={isExiting}
      showMenu
      onMenuClick={handleClose}
      showActions={hasPrimaryAction || hasSecondaryAction}
      primaryAction={
        hasPrimaryAction
          ? {
              label: notification.actionButtonText,
              onClick: () => {
                if (notification.onAction) {
                  notification.onAction()
                } else {
                  onAction?.(notification.id)
                }
                handleClose()
              },
            }
          : undefined
      }
      secondaryAction={
        hasSecondaryAction
          ? {
              label: notification.secondaryButtonText ?? '',
              onClick: () => {
                notification.onSecondaryAction?.()
                handleClose()
              },
            }
          : undefined
      }
      icon={
        notification.icon ? (
          <i className={`mgc ${notification.icon}`} aria-hidden="true" />
        ) : undefined
      }
    />
  )
}

export default NotificationToast
